import { HowToAnswer } from "./HowToAnswer";
import { useMemo, useState } from "react";
import { motion } from "framer-motion";

/**
 * LikertBlock — the ported v1 interaction: a short block of statements, each
 * answered on one 5-point agree/disagree scale, one Continue for the block.
 *
 * Why this exists in a product whose rules say "never a Likert screen for a
 * trait": the v1 instrument is being carried over verbatim, and its items are
 * written for this scale. Everything the scale produces enters the engine
 * through a hard-capped nudge (see `v1port/nudge.ts`), so it colours the
 * narrative and can break a tie without ever deciding a result.
 *
 * Accessibility: each row is a real radiogroup, every option is a 44px
 * minimum target, arrow keys move within a row, and the scale is labelled in
 * words as well as position — never by colour alone.
 */

export interface LikertItem {
  /** Stable id the caller stores the answer under. */
  id: string | number;
  /** The statement, already stemmed for the learner's band. */
  prompt: string;
}

export interface LikertBlockProps {
  items: LikertItem[];
  /** Five labels, disagree → agree. */
  scale: readonly string[];
  answers?: Record<string, number>;
  onChange?: (answers: Record<string, number>) => void;
  onComplete: (answers: Record<string, number>) => void;
  ctaLabel?: string;
}

/** Short labels for narrow screens. The full label stays in the a11y name. */
const SHORT = ["Strongly\ndisagree", "Disagree", "Neutral", "Agree", "Strongly\nagree"];

export function LikertBlock({
  items,
  scale,
  answers: controlled,
  onChange,
  onComplete,
  ctaLabel = "Continue",
}: LikertBlockProps) {
  const [local, setLocal] = useState<Record<string, number>>(controlled ?? {});
  const answers = controlled ?? local;
  const done = useMemo(
    () => items.every((it) => answers[String(it.id)] !== undefined),
    [items, answers],
  );

  function pick(itemId: string, index: number) {
    const next = { ...answers, [itemId]: index };
    setLocal(next);
    onChange?.(next);
  }

  return (
    <div className="space-y-3">
      <HowToAnswer>Tap one point on the scale for each statement. There are no right answers.</HowToAnswer>
      {items.map((item, i) => {
        const key = String(item.id);
        return (
          <motion.div
            key={key}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.04, duration: 0.22 }}
            className="rounded-2xl border border-border bg-card p-4 md:p-5"
          >
            <div className="text-base md:text-lg text-foreground/90 mb-3">{item.prompt}</div>
            <div
              role="radiogroup"
              aria-label={item.prompt}
              className="grid gap-2"
              style={{ gridTemplateColumns: `repeat(${scale.length}, minmax(0, 1fr))` }}
            >
              {scale.map((label, idx) => {
                const active = answers[key] === idx;
                return (
                  <button
                    key={label}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    aria-label={label}
                    onClick={() => pick(key, idx)}
                    className={[
                      "min-h-[44px] rounded-xl border px-1 py-2 text-[11px] md:text-xs",
                      "whitespace-pre-line leading-tight transition-colors",
                      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                      active
                        ? "border-primary bg-primary/15 text-foreground font-medium"
                        : "border-border bg-background text-muted-foreground hover:border-primary/50",
                    ].join(" ")}
                  >
                    <span aria-hidden="true">{SHORT[idx] ?? label}</span>
                  </button>
                );
              })}
            </div>
          </motion.div>
        );
      })}

      <div className="pt-2">
        <button
          type="button"
          disabled={!done}
          onClick={() => onComplete(answers)}
          className={[
            "w-full min-h-[48px] rounded-2xl px-6 text-base font-medium transition-colors",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            done
              ? "bg-primary text-primary-foreground hover:bg-primary/90"
              : "bg-muted text-muted-foreground cursor-not-allowed",
          ].join(" ")}
        >
          {ctaLabel}
        </button>
        {!done && (
          <p className="mt-2 text-center text-xs text-muted-foreground">
            Answer each one to keep going. There are no wrong answers here.
          </p>
        )}
      </div>
    </div>
  );
}
